import React, { useEffect, useMemo, useState } from "react";
import { ScheduleDateType } from "../scheduledDateDropdown/scheduledDateDropdown";
import { ScheduleTimeType } from "../scheduledTimeDropdown/scheduledTimeDropdown";
import { useCookies } from "react-cookie";
import { useRouter } from "next13-progressbar";
import { DateTime } from "luxon";
import { usePathname } from "next/navigation";
import { RestaurantCartScheduledOrderForm } from "./restaurantCartScheduledOrderForm";
import { RestaurantCartItemView } from "./restaurantCartItemView";
import { OrderType } from "@/types";
import { calculatePickupTime } from "@/utils/restaurantCartUtils";
import { isBusinessOpen } from "@/utils/isBusinessOpenUtil";
import {
  getFromLocalStorage,
  saveToLocalStorage,
} from "@/utils/localStorageUtils";
import { RetailCartItem } from "@/dto/retailCartItem";
import { ScheduledTime } from "@/dto/scheduledTime";
import { Address } from "@/dto/address";
import {
  getFromSessionStorage,
  saveToSessionStorage,
} from "@/utils/sessionStorageUtils";
import { GiftCard } from "@/dto/giftCard";
import { Service } from "@/dto/service";
import { LocalStorageKey, SessionStorageKey } from "@/utils/storageKeys";
import { DrawerModal } from "../modals/modals";
import getOrderEstimate from "@/dataLayer/getOrderEstimate";
import { cloverColorTheme } from "@/utils/cloverColorTheme";

const extractHashFromUrl = (url: string) => {
  const urlObj = new URL(url);
  return urlObj.hash;
};

export const RestaurantCartDrawer = ({
  isOpen,
  onClose,
  onRequestClose,
  merchant,
  onTriggerLoginModal,
  setDeliveryAddressModalOpen,
  setIsRestaurantCartOpen,
  setDeliveryAddressErrorModalOpen,
}: any) => {
  const hash =
    typeof window !== "undefined"
      ? extractHashFromUrl(window?.location?.href)
      : "";
  const pathName = usePathname();
  const merchantMeta = merchant.merchantMeta;
  const merchantColor =
    merchantMeta?.colorPalette?.color || cloverColorTheme.Main.primaryColor;
  const merchantService = merchant.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const curbsideService = merchant.services.find(
    (s: Service) => s.type === "CURBSIDE"
  );
  const deliveryService = merchant.services.find(
    (s: Service) => s.type === "DELIVERY"
  );
  const now = DateTime.now().setZone(merchant?.timezone);
  const [cookies] = useCookies(["session"]);
  const router = useRouter();
  const [cartItems, setCartItems] = useState(() => {
    return getFromLocalStorage<RetailCartItem[]>(LocalStorageKey.CART, []);
  });
  const [scheduledOrder, setScheduledOrder] = useState<any>();
  const [orderType, setOrderType] = useState<OrderType>(() => {
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    const defaultOrderType = sessionTime?.orderType || OrderType.PICKUP;
    const restaurantServices = [
      { type: OrderType.PICKUP, service: merchantService },
      { type: OrderType.CURBSIDE, service: curbsideService },
      { type: OrderType.DELIVERY, service: deliveryService },
    ];
    const enabledServices = restaurantServices.filter(
      ({ service }) => service?.enabled
    );

    if (enabledServices.length >= 1) {
      return enabledServices[0].type as OrderType;
    }
    return defaultOrderType;
  });
  const [showScheduledOrderForm, setShowScheduledOrderForm] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<ScheduleDateType>({
    displayValue: "Today",
    dateObject: now.toJSDate(),
  });
  const [scheduleTime, setScheduleTime] = useState<ScheduleTimeType>({
    displayValue: "",
    timeObject: new Date(now.toJSDate()),
  });
  const [giftCard, setGiftCard] = useState<any>(null);
  const businessHours = merchantService?.hours;
  const allDaysEmpty =
    businessHours &&
    Object.values(businessHours).every((day: any) => day.length === 0);
  const isMinimumMet = () => {
    const totalPrice =
      cartItems.reduce(
        (total: number, i: any) => (total += i.value.total * i.value.quantity),
        0
      ) / 100;

    return merchant.deliveryMinimum / 100 <= totalPrice;
  };
  const businessOpen = useMemo(() => isBusinessOpen(merchant), [merchant]);
  const hasItemsInCart = cartItems.length > 0;
  let checkoutDisabled =
    (cartItems.length == 0 && !giftCard) ||
    (!businessOpen && hasItemsInCart) ||
    !businessHours ||
    allDaysEmpty ||
    (cartItems.length > 0 &&
      orderType === OrderType.DELIVERY &&
      !isMinimumMet());
  const customerAuthEnabled: boolean =
    process.env.NEXT_PUBLIC_AUTH_ENABLED === "true";

  useEffect(() => {
    const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
      SessionStorageKey.SCHEDULED_TIME,
      {}
    );
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      date: sessionTime?.date || null,
      time: sessionTime?.time || null,
    });
    const currentAddress = getFromLocalStorage<Partial<Address>>(
      LocalStorageKey.DELIVERY_ADDRESS,
      {}
    );
    const fetchEstimate = async () => {
      if (orderType === OrderType.DELIVERY) {
        if (!currentAddress?.address1) {
          setDeliveryAddressModalOpen(true);
          setIsRestaurantCartOpen(false);
        } else {
          try {
            const result = await getOrderEstimate(merchant.id, currentAddress);
            saveToSessionStorage(SessionStorageKey.ORDER_ESTIMATES, result);
            window.dispatchEvent(
              new StorageEvent("storage", {
                key: "restaurant-cart",
                newValue: JSON.stringify(cartItems),
              })
            );
          } catch {
            setDeliveryAddressModalOpen(false);
            setDeliveryAddressErrorModalOpen(true);
            onRequestClose();
          }
        }
      }
    };
    fetchEstimate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  useEffect(() => {
    if (isOpen && businessHours) {
      const currentDateTime = DateTime.fromJSDate(now.toJSDate(), {});
      const pickupTimeData = calculatePickupTime(
        currentDateTime,
        businessHours,
        merchant.leadTime,
        merchant.timezone,
        orderType
      );

      setScheduledOrder(pickupTimeData);
      saveToSessionStorage(SessionStorageKey.PICKUP_TIME, pickupTimeData);
    }
  }, [isOpen, businessHours, merchant.leadTime, merchant.timezone]);

  useEffect(() => {
    // this code works only when window storage event was dispatched from within
    // RestaurantItemModalModifierSelection component, handleAddToCart function
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === "restaurant-cart") {
        const updatedCart = event.newValue ? JSON.parse(event.newValue) : [];
        setCartItems(updatedCart);
        setIsRestaurantCartOpen(true);
      } else if (event.key === "delivery-order-type") {
        setOrderType(OrderType.DELIVERY);
      } else if (event.key === "pickup-order-type") {
        setOrderType(OrderType.PICKUP);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isOpen) {
      const storedCart = getFromLocalStorage<RetailCartItem[]>(
        LocalStorageKey.CART,
        []
      );
      const sessionTime = getFromSessionStorage<Partial<ScheduledTime>>(
        SessionStorageKey.SCHEDULED_TIME,
        {}
      );
      if (storedCart) {
        setCartItems(storedCart);
      }
      setOrderType(sessionTime?.orderType || OrderType.PICKUP);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  const removeFromCart = (event: React.MouseEvent, index: any) => {
    event.stopPropagation();
    setCartItems((prevCartItems: any[]) =>
      prevCartItems.filter((_: any, i: any) => i !== index)
    );
  };

  useEffect(() => {
    saveToLocalStorage(LocalStorageKey.CART, cartItems);
  }, [cartItems]);

  useEffect(() => {
    const storedGiftCard = getFromLocalStorage<GiftCard | null>(
      LocalStorageKey.GIFT_CARD,
      null
    );
    setGiftCard(storedGiftCard);
  }, [isOpen]);

  const handleBackToCart = () => {
    setShowScheduledOrderForm(false);

    const currentDateTime = DateTime.fromJSDate(now.toJSDate(), {});
    const pickupTimeData = calculatePickupTime(
      currentDateTime,
      businessHours,
      merchant.leadTime,
      merchant.timezone,
      orderType
    );

    setScheduledOrder(pickupTimeData);
    saveToSessionStorage(SessionStorageKey.PICKUP_TIME, pickupTimeData);
  };

  const handleConfirmScheduledOrder = () => {
    saveToSessionStorage(SessionStorageKey.SCHEDULED_TIME, {
      orderType: orderType,
      date: scheduleDate,
      time: scheduleTime,
    });

    setShowScheduledOrderForm(false);

    const merchantZoneDate = DateTime.fromJSDate(scheduleDate.dateObject, {
      zone: merchant?.timezone,
    });
    const formattedDate = merchantZoneDate.toFormat("MMM d");
    const merchantZoneTime = DateTime.fromJSDate(scheduleTime.timeObject, {
      zone: merchant?.timezone,
    });
    const formattedTime = merchantZoneTime.toLocaleString(DateTime.TIME_SIMPLE);

    const isToday = DateTime.fromJSDate(scheduleDate.dateObject).hasSame(
      DateTime.now(),
      "day"
    );
    const isTomorrow = DateTime.fromJSDate(scheduleDate.dateObject).hasSame(
      DateTime.now().plus({ days: 1 }),
      "day"
    );

    setScheduledOrder(
      isToday
        ? `Today, ${formattedTime}`
        : isTomorrow
        ? `Tomorrow (${formattedDate}), ${formattedTime}`
        : `${formattedDate}, ${formattedTime}`
    );
  };

  const handleContinueToCheckout = () => {
    if (!customerAuthEnabled) {
      router.push("/guest-checkout");
    } else {
      if (cookies.session) {
        router.push("/checkout");
        onRequestClose();
      } else {
        onRequestClose();
        onTriggerLoginModal(true);
      }
    }
  };

  return (
    <DrawerModal
      isOpen={isOpen}
      onRequestClose={() => {
        setShowScheduledOrderForm(false);
        if (hash !== undefined) {
          window.history.replaceState({}, "", pathName);
        }
        onRequestClose();
      }}
    >
      <div className="flex flex-col h-full w-[100%]">
        {showScheduledOrderForm ? (
          <RestaurantCartScheduledOrderForm
            {...{
              merchant,
              businessHours,
              handleBackToCart,
              scheduleDate,
              setScheduleDate,
              allDaysEmpty,
              scheduleTime,
              setScheduleTime,
              merchantColor,
              handleConfirmScheduledOrder,
              orderType,
            }}
          />
        ) : (
          <RestaurantCartItemView
            {...{
              onClose,
              merchant,
              hash,
              cartItems,
              onRequestClose,
              orderType,
              setOrderType,
              scheduledOrder,
              setScheduledOrder,
              setShowScheduledOrderForm,
              removeFromCart,
              checkoutDisabled,
              merchantColor,
              handleContinueToCheckout,
              setIsRestaurantCartOpen,
              giftCard,
              setGiftCard,
            }}
          />
        )}
      </div>
    </DrawerModal>
  );
};
